/**
 * F1/F5/F6 (tier2-approval-requests) — клиент заявок на согласование.
 *
 * Tier 2 перестал быть полем в запросе инициатора: диалог больше не делает вид, что
 * подписывает за другого, а отправляет намерение названному человеку. Объект при
 * этом НЕ меняется — рядом появляется метка «ждёт согласования» (§6).
 */
import {
  type ApprovalRequestReassignInput,
  ApprovalRequestRow,
  type ApprovalRequestWithdrawInput,
} from '@staiter/shared';
import { z } from 'zod';
import { apiRequest } from './client';

const RequestEnvelope = z.object({ request: ApprovalRequestRow });

/** Типы действий, которые пак перевёл на заявку. */
export type ApprovalRequestTaskTypeT =
  | 'project_cancel'
  | 'project_uncancel'
  | 'project_archive'
  | 'project_restore'
  | 'project_complete'
  | 'project_close'
  | 'project_plan_shift';

export async function createApprovalRequest(body: {
  task_type: ApprovalRequestTaskTypeT;
  subject_kind: 'project';
  subject_id: string;
  addressee_user_id: string;
  reason: string;
  /** Тело действия сверх причины: новые даты у переноса срока. */
  payload?: Record<string, unknown>;
}) {
  const data = await apiRequest('/approval-requests', RequestEnvelope, {
    method: 'POST',
    body: { ...body, payload: body.payload ?? {} },
  });
  return data.request;
}

/** F5 — адресат недоступен: инициатор переводит заявку другому кандидату. */
export async function reassignApprovalRequest(
  id: string,
  body: z.infer<typeof ApprovalRequestReassignInput>,
) {
  const data = await apiRequest(
    `/approval-requests/${encodeURIComponent(id)}/reassign`,
    RequestEnvelope,
    { method: 'POST', body },
  );
  return data.request;
}

/** F6 — передумал: отзыв собственного намерения не Tier 2 и подписи не требует. */
export async function withdrawApprovalRequest(
  id: string,
  body: z.infer<typeof ApprovalRequestWithdrawInput> = {},
) {
  const data = await apiRequest(
    `/approval-requests/${encodeURIComponent(id)}/withdraw`,
    RequestEnvelope,
    { method: 'POST', body },
  );
  return data.request;
}

/**
 * F30 (T2A-F16; B40) — инициатор напоминает решающему. Сервер пускает раз в сутки;
 * чаще — 429 `APPROVAL_REMIND_TOO_SOON` с `next_allowed_at`.
 */
const RemindResult = z
  .object({
    id: z.string(),
    reminded_at: z.string().datetime(),
    next_allowed_at: z.string().datetime(),
  })
  .passthrough();

export async function remindApprovalRequest(id: string) {
  return apiRequest(`/approval-requests/${encodeURIComponent(id)}/remind`, RemindResult, {
    method: 'POST',
  });
}
